import { type EbnEventRow, type EbnSportRow, type EbnView, type EbnWarning, ebnWarnings, impliedAthletes } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { invalidateEntries, putEbn } from "../../lib/api/entries";
import { type SportHistory, sportHistory } from "../../lib/entries/medal-history";
import { toast } from "../../lib/ui/toast";
import { ChevronDownIcon } from "../accreditation/icons";
import { EmptyCard } from "../common/empty-card";
import { joinList } from "../fnb/parts";
import { GenderTag } from "../shortlist/parts";
import { AlertIcon, InfoIcon } from "../transport/icons";
import { Celebration, Scoreboard, Stepper } from "./board";
import { QualifyingLine, TypeChip, useQuotaText } from "./event-facts";
import { TierBadge, useMedalHistory } from "./history";
import {
	type EbnDraft,
	clampNumber,
	ebnClearSport,
	ebnDirtyEvents,
	ebnDraftFromEoi,
	ebnEnterAll,
	ebnEventMatches,
	ebnFromEoi,
	ebnMaxMedals,
	ebnSportCounts,
	ebnSportsWithDraft,
	ebnValue,
	eventCap,
	eventsOf,
} from "./model";
import { LeaveGuard, ReopenSheet } from "./parts";
import { EbnReviewContent, EntrySummary, ReviewSheet } from "./review-sheet";
import { SearchGlyph } from "./eoi-editor";

/** Sports of the EOI start open when there are at most this many; more start folded. */
const EBN_OPEN_MAX = 3;

/**
 * Entry by number: one card per sport, folded or open (Expand all /
 * Collapse all), each event a row with its facts on the left and the
 * number on the right. The sports of the EOI come first, their EOI event
 * picks highlighted with "you estimated N"; then EVERY other sport of the
 * games in a folded "Other sports" group — the second chance: a number
 * there adds the sport to the EOI when saved (the API does it in the same
 * statement). An EBN with nothing saved yet STARTS from the EOI's expected
 * entries (user, 2026-10-30: typing the same numbers twice was the
 * complaint) — a draft (no banner, user 2026-10-30), saved only when
 * the NOC saves. One search filters events across every sport. Warnings
 * are `ebnWarnings` (contracts — the API's rule) over the draft, so they
 * answer as you type. Save sends each changed sport's `PUT /entries/ebn`
 * one after the other (never a burst). When the API says `can_edit` is
 * false the page is the summary.
 */
export function EbnEditor({ view, canReopen, search }: { view: EbnView; canReopen: boolean; search: { games_id?: string; org?: string } }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const [draft, setDraft] = useState<EbnDraft>({});
	const [saving, setSaving] = useState(false);
	const [dialog, setDialog] = useState<"review" | "reopen" | null>(null);
	const [party, setParty] = useState(false);
	const [editFinal, setEditFinal] = useState(false);
	const [othersOpen, setOthersOpen] = useState(false);
	const [query, setQuery] = useState("");
	const fromEoi = view.sports.filter((s) => s.in_eoi);
	const others = view.sports.filter((s) => !s.in_eoi);
	const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(fromEoi.length <= EBN_OPEN_MAX ? fromEoi.map((s) => s.sport_id) : []));
	/** Sports this session's saves added to the EOI (`eoi_sport_added`) — the review names them. */
	const [addedToEoi, setAddedToEoi] = useState<readonly string[]>([]);
	const othersId = useId();
	// Admins only (the active role): the NOC's past SEA Games results per sport; null for everyone else.
	const hist = useMedalHistory(view.games_id, view.organization.code);
	const tiers = useMemo(
		() => (hist ? new Map(view.sports.map((s) => [s.sport_id, sportHistory(hist.data, s.code, hist.noc)])) : null),
		[hist, view.sports],
	);

	const dirtyBySport = useMemo(() => new Map(view.sports.map((s) => [s.sport_id, ebnDirtyEvents(s, draft)])), [view.sports, draft]);
	const dirtyCount = [...dirtyBySport.values()].reduce((n, x) => n + x.length, 0);
	const liveWarnings = useMemo(() => ebnWarnings(ebnSportsWithDraft(view.sports, draft)), [view.sports, draft]);
	const savedEntries = view.sports.reduce((n, s) => n + s.entries, 0);
	const isFinal = view.submission.state === "FINAL";

	// A refetch drops the drafts the server now holds (a sport saved, or a colleague's change).
	useEffect(() => {
		setDraft((d) => {
			const next: EbnDraft = {};
			for (const s of view.sports) {
				const mine = d[s.sport_id];
				if (!mine) continue;
				const keep: Record<string, number> = {};
				for (const e of eventsOf(s)) {
					const v = mine[e.event_id];
					if (v !== undefined && v !== (e.entries ?? 0)) keep[e.event_id] = v;
				}
				if (Object.keys(keep).length) next[s.sport_id] = keep;
			}
			return next;
		});
	}, [view.sports]);

	// Nothing saved yet: start from the EOI's expected entries, once per delegation.
	const prefilledFor = useRef<string | null>(null);
	const delegation = `${view.games_id}:${view.organization.id}`;
	useEffect(() => {
		if (prefilledFor.current === delegation) return;
		prefilledFor.current = delegation;
		if (!view.can_edit || isFinal || savedEntries > 0) return;
		const r = ebnDraftFromEoi(view.sports, {});
		if (r.filled === 0) return;
		setDraft(r.draft);
	}, [delegation, view.can_edit, isFinal, savedEntries, view.sports]);

	const locked = !view.can_edit || saving;
	const scope = { games_id: view.games_id, organization_id: view.organization.id };

	const setValue = (sportId: string, eventId: string, v: number) => setDraft((d) => ({ ...d, [sportId]: { ...d[sportId], [eventId]: v } }));
	const setSport = (sportId: string, values: Record<string, number>) => setDraft((d) => ({ ...d, [sportId]: values }));
	const toggle = (sportId: string, on: boolean) =>
		setOpen((cur) => {
			const next = new Set(cur);
			if (on) next.add(sportId);
			else next.delete(sportId);
			return next;
		});

	const saveAll = async (thenReview = false) => {
		setSaving(true);
		let saved = 0;
		let failed = false;
		const added: string[] = [];
		// Sequential on purpose: one transaction per sport, never a burst.
		for (const s of view.sports) {
			if ((dirtyBySport.get(s.sport_id)?.length ?? 0) === 0) continue;
			try {
				const res = await putEbn({ ...scope, sport_id: s.sport_id, entries: ebnBodyFor(s) });
				saved += 1;
				if (res.eoi_sport_added) added.push(s.sport_id);
			} catch (err) {
				toast.error(err);
				failed = true;
				break;
			}
		}
		await invalidateEntries();
		setSaving(false);
		if (added.length > 0) {
			setAddedToEoi((cur) => [...new Set([...cur, ...added])]);
			const names = added.map((id) => view.sports.find((s) => s.sport_id === id)?.name ?? id);
			toast.success(t("ebn.addedToEoi", { count: names.length, list: joinList(names) }));
		} else if (saved > 0) toast.success(t("save.done"));
		if (thenReview && !failed) setDialog("review");
	};
	const ebnBodyFor = (s: EbnSportRow) =>
		eventsOf(s)
			.map((e) => ({ event_id: e.event_id, entries: ebnValue(draft, s.sport_id, e) }))
			.filter((r) => r.entries > 0);

	// Live totals (the board) over every sport with the draft applied; the EOI estimate over the EOI's sports.
	const live = view.sports.reduce(
		(acc, s) => {
			const c = ebnSportCounts(s, draft);
			return {
				entered: acc.entered + c.entered,
				entries: acc.entries + c.entries,
				implied: acc.implied + c.implied,
				estimate: acc.estimate + (s.eoi.est_athletes_men ?? 0) + (s.eoi.est_athletes_women ?? 0),
			};
		},
		{ entered: 0, entries: 0, implied: 0, estimate: 0 },
	);

	const dialogs = (
		<>
			<LeaveGuard dirtyCount={dirtyCount} />
			<ReviewSheet
				open={dialog === "review"}
				phase="EBN"
				orgName={view.organization.name}
				gamesId={view.games_id}
				organizationId={view.organization.id}
				canSubmit={view.can_submit}
				onClose={() => setDialog(null)}
				onDone={() => setParty(true)}
			>
				<EbnReviewContent view={view} addedToEoi={addedToEoi} />
			</ReviewSheet>
			<ReopenSheet
				open={dialog === "reopen"}
				phase="EBN"
				gamesId={view.games_id}
				organizationId={view.organization.id}
				orgName={view.organization.name}
				onClose={() => setDialog(null)}
			/>
			{party && <Celebration phase="EBN" search={search} onClose={() => setParty(false)} />}
		</>
	);

	if (view.sports.length === 0) return <EmptyCard title={t("ebn.noSportsTitle")}>{t("ebn.noSportsHint")}</EmptyCard>;

	// Read-only, or submitted (an admin may still choose to change it): the summary.
	if (!view.can_edit || (isFinal && !editFinal && dirtyCount === 0))
		return (
			<div className="flex min-w-0 flex-col gap-[0.875rem] pb-[1rem]">
				<EntrySummary
					phase="EBN"
					view={view}
					onEdit={view.can_edit ? () => setEditFinal(true) : undefined}
					onReopen={isFinal && canReopen ? () => setDialog("reopen") : undefined}
				>
					<EbnReviewContent view={view} showWarnings={false} />
				</EntrySummary>
				{dialogs}
			</div>
		);

	const q = query.trim();
	const searching = q !== "";
	/** The sport's events the search leaves (every event when not searching). */
	const shownEvents = (s: EbnSportRow) => (searching ? new Set(eventsOf(s).filter((e) => ebnEventMatches(q, s, e)).map((e) => e.event_id)) : null);
	const matches = (s: EbnSportRow) => !searching || eventsOf(s).some((e) => ebnEventMatches(q, s, e));
	const fromEoiShown = fromEoi.filter(matches);
	const othersShown = others.filter(matches);
	const othersWithEntries = others.filter((s) => ebnSportCounts(s, draft).entered > 0).length;
	const foldable = [...fromEoi, ...(othersOpen ? others : [])].map((s) => s.sport_id);
	const allOpen = foldable.length > 0 && foldable.every((id) => open.has(id));
	const noneOpen = foldable.every((id) => !open.has(id));

	const card = (s: EbnSportRow) => (
		<SportCard
			key={s.sport_id}
			sport={s}
			draft={draft}
			only={shownEvents(s)}
			dirty={dirtyBySport.get(s.sport_id)?.length ?? 0}
			warnings={liveWarnings.filter((w) => w.sport_id === s.sport_id)}
			locked={locked}
			tier={tiers?.get(s.sport_id)}
			// A search opens every sport it matches; the folds come back when it is cleared.
			open={searching || open.has(s.sport_id)}
			onToggle={(on) => toggle(s.sport_id, on)}
			onValue={(eventId, v) => setValue(s.sport_id, eventId, v)}
			onEnterAll={() => setSport(s.sport_id, ebnEnterAll(s, draft))}
			onFromEoi={() => setSport(s.sport_id, ebnFromEoi(s, draft).values)}
			onClear={() => setSport(s.sport_id, ebnClearSport(s))}
		/>
	);

	return (
		<div className="flex min-w-0 flex-col gap-[0.875rem] pb-[1rem]">
			<div className="ent-eb-tools">
				<label className="ent-search ent-eb-tools__search">
					<SearchGlyph />
					<input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("ebn.search")} aria-label={t("ebn.search")} />
				</label>
				<span className="ent-eb-tools__legend">
					<span className="ent-eb-tools__swatch" aria-hidden />
					{t("ebn.sheet.legend")}
				</span>
				<span className="ent-eb-tools__fold">
					<button type="button" className="btn btn-ghost btn-sm" disabled={searching || allOpen} onClick={() => setOpen(new Set([...open, ...foldable]))}>
						{t("review.expandAll")}
					</button>
					<button type="button" className="btn btn-ghost btn-sm" disabled={searching || noneOpen} onClick={() => setOpen(new Set())}>
						{t("review.collapseAll")}
					</button>
				</span>
			</div>

			<section className="grid gap-[0.625rem]" aria-labelledby="ebn-from-eoi-h">
				<h2 id="ebn-from-eoi-h" className="ent-eb-grouph">
					{t("ebn.sheet.fromEoi")}
					<span className="ent-sheet__n">{fmt(fromEoi.length)}</span>
				</h2>
				{fromEoi.length === 0 ? (
					<p className="ent-sheet__empty">{t("ebn.sheet.fromEoiEmpty")}</p>
				) : fromEoiShown.length === 0 ? (
					<p className="ent-sheet__empty">{t("ebn.noMatch", { q })}</p>
				) : (
					fromEoiShown.map(card)
				)}
			</section>

			{others.length > 0 && (!searching || othersShown.length > 0) && (
				<section className="grid gap-[0.625rem]">
					<button
						type="button"
						className="ent-sheet__toggle"
						aria-expanded={searching || othersOpen}
						aria-controls={othersId}
						disabled={searching}
						onClick={() => setOthersOpen((v) => !v)}
					>
						<ChevronDownIcon className="ent-sheet__chev size-[1rem]" aria-hidden />
						<span className="grid min-w-0 gap-[0.125rem] text-left">
							<span className="ent-eb-grouph">
								{t("ebn.sheet.others")}
								<span className="ent-sheet__n">{fmt(others.length)}</span>
								{othersWithEntries > 0 && <span className="ent-badge ent-badge--add">{t("ebn.sheet.othersEntered", { count: othersWithEntries, n: fmt(othersWithEntries) })}</span>}
							</span>
							<span className="text-[0.75rem] font-normal text-gms-ink-3">{t("ebn.sheet.othersHint")}</span>
						</span>
					</button>
					{(searching || othersOpen) && (
						<div id={othersId} className="grid gap-[0.625rem]">
							{othersShown.map(card)}
						</div>
					)}
				</section>
			)}

			<Scoreboard
				stats={[
					{ key: "events", label: t("ebn.tiles.events"), value: live.entered },
					{ key: "entries", label: t("ebn.tiles.entries"), value: live.entries },
					{ key: "implied", label: t("ebn.tiles.implied"), value: live.implied, note: t("ebn.tiles.estimate", { n: fmt(live.estimate) }) },
				]}
				pendingCount={dirtyCount}
				saving={saving}
				isFinal={isFinal}
				submission={view.submission}
				canSubmit={view.can_submit}
				submitDisabledReason={savedEntries === 0 ? t("bar.submitNeeds") : undefined}
				canReopen={isFinal && canReopen}
				onDiscard={() => setDraft({})}
				onSave={() => void saveAll()}
				onReview={() => setDialog("review")}
				onSaveAndReview={!isFinal ? () => void saveAll(true) : undefined}
				onReopen={() => setDialog("reopen")}
			/>
			{dialogs}
		</div>
	);
}

type AboveWarning = Extract<EbnWarning, { kind: "ABOVE_ESTIMATE" }>;

/** One sport: a head that folds it (live progress, entries, athletes) and its events. */
function SportCard({
	sport: s,
	draft,
	only,
	dirty,
	warnings,
	locked,
	tier,
	open,
	onToggle,
	onValue,
	onEnterAll,
	onFromEoi,
	onClear,
}: {
	sport: EbnSportRow;
	draft: EbnDraft;
	/** The events a search leaves; null = all. */
	only: ReadonlySet<string> | null;
	dirty: number;
	warnings: readonly EbnWarning[];
	locked: boolean;
	tier: SportHistory | undefined;
	open: boolean;
	onToggle: (on: boolean) => void;
	onValue: (eventId: string, v: number) => void;
	onEnterAll: () => void;
	onFromEoi: () => void;
	onClear: () => void;
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const c = ebnSportCounts(s, draft);
	const dash = (n: number | null) => (n === null ? "—" : fmt(n));
	const willAdd = warnings.some((w) => w.kind === "NOT_IN_EOI");
	const below = warnings.find((w): w is Extract<EbnWarning, { kind: "BELOW_ESTIMATE" }> => w.kind === "BELOW_ESTIMATE");
	const without = warnings.some((w) => w.kind === "SPORT_WITHOUT_EVENTS");
	const above = (eventId: string) => warnings.find((w): w is AboveWarning => w.kind === "ABOVE_ESTIMATE" && w.event_id === eventId);
	const bodyId = useId();
	const disciplines = s.disciplines
		.map((d) => ({ ...d, events: only ? d.events.filter((e) => only.has(e.event_id)) : d.events }))
		.filter((d) => d.events.length > 0);
	const multi = disciplines.length > 1;
	const anyEmpty = eventsOf(s).some((e) => e.is_active && ebnValue(draft, s.sport_id, e) === 0 && eventCap(e) > 0);
	const fromEoi = ebnFromEoi(s, draft).filled;
	const pct = c.events === 0 ? 0 : Math.round((c.entered / c.events) * 100);

	return (
		<section className="ent-eb" data-in-eoi={s.in_eoi || undefined} data-open={open || undefined} aria-label={s.name}>
			<button type="button" className="ent-eb__head" aria-expanded={open} aria-controls={bodyId} onClick={() => onToggle(!open)}>
				<span className="ent-eb__badge" data-on={c.entered > 0 || undefined}>
					{s.code}
				</span>
				<span className="ent-eb__title">
					<b className="ent-eb__name">{s.name}</b>
					{tier && <TierBadge history={tier} />}
					{willAdd && <span className="ent-badge ent-badge--add">{t("ebn.willAdd")}</span>}
					{dirty > 0 && <span className="ent-dot ent-dot--unsaved" title={t("ebn.unsaved", { count: dirty, n: fmt(dirty) })} />}
				</span>
				<span className="ent-eb__figs">
					<span className="ent-eb__fig">
						<b>
							{fmt(c.entered)}
							<small>/{fmt(c.events)}</small>
						</b>
						<em>{t("ebn.tiles.events")}</em>
					</span>
					<span className="ent-eb__fig">
						<b>{fmt(c.entries)}</b>
						<em>{t("ebn.tiles.entries")}</em>
					</span>
					<span className="ent-eb__fig">
						<b>{fmt(c.implied)}</b>
						<em>{t("ebn.tiles.implied")}</em>
					</span>
				</span>
				<ChevronDownIcon className="ent-eb__chev size-[1.125rem] shrink-0" aria-hidden />
				<span className="ent-eb__bar" aria-hidden>
					<span style={{ width: `${pct}%` }} />
				</span>
			</button>
			{open && (
				<div id={bodyId} className="ent-eb__body">
					<div className="ent-eb__tools">
						{s.in_eoi && (
							<span className="mr-auto text-[0.75rem] text-gms-ink-2">
								{t("ebn.sheet.eoiLine", { m: dash(s.eoi.est_athletes_men), w: dash(s.eoi.est_athletes_women), o: dash(s.eoi.est_officials) })}
							</span>
						)}
						<span className={`flex flex-wrap gap-[0.375rem] ${s.in_eoi ? "" : "ml-auto"}`}>
							{fromEoi > 0 && (
								<button type="button" className="btn btn-xs btn-line" onClick={onFromEoi} disabled={locked} title={t("ebn.useEoiHint", { count: fromEoi, n: fmt(fromEoi) })}>
									{t("ebn.useEoi")}
								</button>
							)}
							<button type="button" className="btn btn-xs btn-line" onClick={onEnterAll} disabled={locked || !anyEmpty} title={t("ebn.enterAllHint")}>
								{t("ebn.enterAll")}
							</button>
							<button type="button" className="btn btn-xs btn-line" onClick={onClear} disabled={locked || c.entered === 0}>
								{t("ebn.clearSport")}
							</button>
						</span>
					</div>
					{below && (
						<p className="ent-hint" role="status">
							<AlertIcon aria-hidden className="mt-[0.0625rem] size-[0.875rem] shrink-0" />
							{t("warning.belowInline", { count: below.implied_athletes, implied: fmt(below.implied_athletes), estimated: fmt(below.estimated) })}
						</p>
					)}
					{without && (
						<p className="ent-hint" role="status">
							<AlertIcon aria-hidden className="mt-[0.0625rem] size-[0.875rem] shrink-0" />
							{t("warning.withoutInline")}
						</p>
					)}
					{willAdd && (
						<p className="ent-hint ent-hint--info" role="status">
							<InfoIcon aria-hidden className="mt-[0.0625rem] size-[0.875rem] shrink-0" />
							{t("ebn.willAddHint", { sport: s.name })}
						</p>
					)}
					{c.events === 0 ? (
						<p className="ent-sheet__empty">{t("ebn.noEvents")}</p>
					) : (
						disciplines.map((d) => (
							<div key={d.discipline_id} className="ent-eb__group">
								{multi && <h3 className="ent-eb__disc">{d.name}</h3>}
								<ul className="ent-eb__list">
									{d.events.map((e) => (
										<EventRow
											key={e.event_id}
											event={e}
											value={ebnValue(draft, s.sport_id, e)}
											warn={above(e.event_id)}
											locked={locked}
											onValue={(v) => onValue(e.event_id, v)}
										/>
									))}
								</ul>
							</div>
						))
					)}
					{!s.is_active && <p className="text-[0.71875rem] text-gms-ink-3">{t("ebn.inactiveSport")}</p>}
				</div>
			)}
		</section>
	);
}

/** One event: name and facts on the left; the number and the athletes it implies on the right. */
function EventRow({
	event: e,
	value,
	warn,
	locked,
	onValue,
}: {
	event: EbnEventRow;
	value: number;
	warn: AboveWarning | undefined;
	locked: boolean;
	onValue: (v: number) => void;
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const quotaText = useQuotaText();
	const cap = eventCap(e);
	const changed = value !== (e.entries ?? 0);
	const max = ebnMaxMedals(e, value);
	const quota = quotaText(e);
	const ghost = e.eoi_event?.est_entries ?? null;
	const implied = value > 0 ? impliedAthletes(e, value) : 0;
	return (
		<li className="ent-ebev" data-eoi={!!e.eoi_event || undefined} data-on={value > 0 || undefined} data-dirty={changed || undefined}>
			<div className="ent-ebev__main">
				<span className="ent-ebev__name">{e.name}</span>
				<span className="ent-ebev__facts">
					<GenderTag gender={e.gender} />
					<TypeChip event={e} />
					{quota && <span className="ent-quota">{quota}</span>}
					{max !== null && <span className="ent-ebev__mm">{t("ebn.maxMedals", { count: max, n: fmt(max) })}</span>}
					<QualifyingLine event={e} />
				</span>
				{e.eoi_event && <span className="ent-ebev__eoi">{ghost !== null ? t("ebn.youEstimated", { count: ghost, n: fmt(ghost) }) : t("ebn.inYourEoi")}</span>}
				{warn && (
					<span className="ent-sheet__warn">
						<AlertIcon aria-hidden className="size-[0.8125rem] shrink-0" />
						{t("warning.aboveInline", { count: warn.needed, needed: fmt(warn.needed), estimated: fmt(warn.estimated) })}
					</span>
				)}
			</div>
			<div className="ent-ebev__num">
				<Stepper
					value={value}
					max={cap}
					locked={locked}
					tone={value >= cap && cap > 0 && e.max_entries_per_noc !== null ? "full" : undefined}
					label={t("ebn.numberLabel", { event: e.name })}
					decLabel={t("ebn.decrease", { event: e.name })}
					incLabel={t("ebn.increase", { event: e.name })}
					parse={(raw) => clampNumber(raw, cap)}
					onValue={(v) => onValue(clampNumber(v, cap))}
				/>
				<span className="ent-ebev__unit">
					{value > 0
						? e.competitor_type === "TEAM"
							? t("ebn.teamsAthletes", { count: value, teams: fmt(value), n: fmt(implied) })
							: t("ebn.athletesCount", { count: implied, n: fmt(implied) })
						: " "}
				</span>
			</div>
		</li>
	);
}
